import React, { useRef, useState } from 'react';
import api from '../api/api';
import Icon from '../components/Icon';
import { sourceIcon } from '../product/productUtils';
import { formatBytes, readFileAs } from './onboardingUtils';

const TILES = [
    { type: 'landing', icon: 'language', label: 'Landing page', placeholder: 'yourproduct.com' },
    { type: 'store', icon: 'apps', label: 'App Store / Play', placeholder: 'apps.apple.com/… or play.google.com/store/apps/…' },
    { type: 'upload', icon: 'upload_file', label: 'Upload docs' },
    { type: 'text', icon: 'notes', label: 'Paste text' },
];

const MAX_PDF_BYTES = 7 * 1024 * 1024;
const TEXT_FILE = /\.(txt|md|markdown|csv)$/i;

const newId = () => `src-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const SourcesStep = ({ sources, setDraft, isAnalyzing, isBusy, onAnalyze, onSkip }) => {
    const [type, setType] = useState('landing');
    const [url, setUrl] = useState('');
    const [text, setText] = useState('');
    const [isOver, setIsOver] = useState(false);
    const [fileError, setFileError] = useState('');
    const fileInput = useRef(null);

    const setSources = (update) => setDraft(prev => ({ ...prev, sources: update(prev.sources) }));
    const patchSource = (id, changes) => setSources(list => list.map(source => (source.id === id ? { ...source, ...changes } : source)));

    const addUrl = async (event) => {
        event.preventDefault();
        const value = url.trim();
        if (!value) return;
        const id = newId();
        setSources(list => [...list, { id, type, url: value, title: value.replace(/^https?:\/\//, ''), detail: 'Reading…', status: 'reading', addedAt: new Date().toISOString() }]);
        setUrl('');
        try {
            const read = await api.readSource(type, value);
            patchSource(id, { title: read.title || value, detail: read.detail || '', url: read.url || value, icon: read.icon || '', text: read.text, status: 'ready' });
        } catch (e) {
            patchSource(id, { status: 'error', detail: e.response?.data?.error || "Couldn't read this page. Check the address or paste the text instead." });
        }
    };

    const addFile = async (file) => {
        if (!file) return;
        setFileError('');
        const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
        if (!isPdf && !TEXT_FILE.test(file.name)) {
            setFileError('Upload a PDF or a text file (.txt, .md, .csv). For slides or Word docs, export to PDF first.');
            return;
        }
        if (isPdf && file.size > MAX_PDF_BYTES) {
            setFileError(`That PDF is ${formatBytes(file.size)}. The limit is 7 MB.`);
            return;
        }
        try {
            const content = await readFileAs(file, isPdf ? 'readAsDataURL' : 'readAsText');
            setSources(list => [...list, {
                id: newId(),
                type: 'upload',
                title: file.name,
                detail: `Upload · ${formatBytes(file.size)}`,
                ...(isPdf ? { file: { name: file.name, size: file.size, dataUrl: content } } : { text: content }),
                status: 'ready',
                addedAt: new Date().toISOString(),
            }]);
        } catch (e) {
            setFileError("Couldn't read that file.");
        }
    };

    const addText = (event) => {
        event.preventDefault();
        const value = text.trim();
        if (!value) return;
        const firstLine = value.split('\n')[0].trim();
        setSources(list => [...list, {
            id: newId(),
            type: 'text',
            title: firstLine.length > 60 ? `${firstLine.slice(0, 57)}…` : firstLine || 'Pasted text',
            detail: `Paste · ${value.split(/\s+/).length.toLocaleString()} words`,
            text: value,
            status: 'ready',
            addedAt: new Date().toISOString(),
        }]);
        setText('');
    };

    const ready = sources.filter(source => source.status === 'ready');
    const isReading = sources.some(source => source.status === 'reading');
    const tile = TILES.find(t => t.type === type);
    // Resumed drafts don't keep uploaded PDFs, so those need uploading again to be re-read
    const needsReupload = (source) => source.type === 'upload' && source.file && !source.file.dataUrl && !source.text;

    return (
        <div className="ls-flow-center">
            <div className="ls-flow-column">
                <div>
                    <h1 className="ls-flow-h1">Where can we learn about your product?</h1>
                    <div className="ls-flow-lead">
                        Add one or more sources. We'll read them, fill in your product profile, then ask only about what's missing.
                    </div>
                </div>

                <div className="ls-source-tiles" role="radiogroup" aria-label="Source type">
                    {TILES.map(t => (
                        <button
                            type="button"
                            key={t.type}
                            role="radio"
                            aria-checked={type === t.type}
                            className={`ls-source-tile${type === t.type ? ' is-selected' : ''}`}
                            onClick={() => {
                                setType(t.type);
                                setFileError('');
                            }}
                        >
                            <Icon name={t.icon} size={22} />
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{t.label}</span>
                        </button>
                    ))}
                </div>

                {(type === 'landing' || type === 'store') && (
                    <form onSubmit={addUrl} style={{ display: 'flex', gap: 10 }}>
                        <label className="ls-input-with-icon">
                            <Icon name="link" color="var(--disabled)" />
                            <input
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder={tile.placeholder}
                                aria-label={`${tile.label} URL`}
                                inputMode="url"
                                autoFocus
                            />
                        </label>
                        <button type="submit" className="ls-btn ls-btn-dark is-square" disabled={!url.trim()}>Add</button>
                    </form>
                )}

                {type === 'upload' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button
                            type="button"
                            className={`ls-dropzone${isOver ? ' is-over' : ''}`}
                            onClick={() => fileInput.current?.click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsOver(true);
                            }}
                            onDragLeave={() => setIsOver(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsOver(false);
                                Array.from(e.dataTransfer.files).forEach(addFile);
                            }}
                            style={{ fontFamily: 'inherit', textAlign: 'left' }}
                        >
                            <Icon name="upload_file" color="var(--disabled)" />
                            Drop a PDF (deck, one-pager) or text file here, or click to choose
                        </button>
                        <input
                            ref={fileInput}
                            type="file"
                            accept=".pdf,.txt,.md,.markdown,.csv,application/pdf,text/plain"
                            multiple
                            hidden
                            onChange={(e) => {
                                Array.from(e.target.files).forEach(addFile);
                                e.target.value = '';
                            }}
                        />
                        {fileError && <span style={{ fontSize: 13, color: 'var(--danger-text)' }}>{fileError}</span>}
                    </div>
                )}

                {type === 'text' && (
                    <form onSubmit={addText} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <textarea
                            className="ls-textarea"
                            rows={5}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Paste a product description, reviews, a pitch, FAQs…"
                            aria-label="Product text"
                            autoFocus
                        />
                        <button type="submit" className="ls-btn ls-btn-dark is-square" style={{ alignSelf: 'flex-end' }} disabled={!text.trim()}>Add</button>
                    </form>
                )}

                {sources.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>
                            {ready.length} source{ready.length === 1 ? '' : 's'} added
                        </div>
                        {isAnalyzing && (
                            <div className="ls-card" style={{ gap: 10, padding: 16 }}>
                                <div style={{ fontSize: 14, fontWeight: 600 }}>Reading your sources and filling in your profile…</div>
                                <div className="ls-indeterminate" />
                                <div style={{ fontSize: 13, color: 'var(--muted)' }}>This usually takes under a minute.</div>
                            </div>
                        )}
                        {sources.map(source => (
                            <div key={source.id} className={`ls-source-row${source.status === 'error' ? ' is-error' : ''}`}>
                                {source.status === 'reading' || (isAnalyzing && source.status === 'ready')
                                    ? <span className="ls-spinner" style={{ margin: 2 }} />
                                    : <Icon name={source.status === 'error' ? 'error' : sourceIcon(source)} color={source.status === 'error' ? 'var(--danger-text)' : 'var(--secondary)'} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="ls-row-title">{source.title}</div>
                                    <div style={{ fontSize: 12, color: source.status === 'error' || needsReupload(source) ? 'var(--danger-text)' : 'var(--muted)' }}>
                                        {needsReupload(source) ? 'Upload this file again to re-analyze it' : isAnalyzing && source.status === 'ready' ? 'Analyzing…' : source.detail}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="ls-chip-remove"
                                    aria-label={`Remove ${source.title}`}
                                    disabled={isAnalyzing}
                                    onClick={() => setSources(list => list.filter(s => s.id !== source.id))}
                                >
                                    <Icon name="close" size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 6, gap: 12, flexWrap: 'wrap' }}>
                    <button type="button" className="ls-link is-muted" onClick={onSkip} disabled={isAnalyzing || isBusy}>
                        Skip and fill in manually
                    </button>
                    <button
                        type="button"
                        className="ls-btn ls-btn-primary is-large"
                        onClick={onAnalyze}
                        disabled={ready.length === 0 || isReading || isAnalyzing || isBusy}
                    >
                        {isAnalyzing ? <span className="ls-spinner" style={{ borderTopColor: 'var(--accent-ink)', borderColor: 'rgba(5, 48, 44, .25)' }} /> : <Icon name="auto_awesome" size={18} />}
                        {isAnalyzing ? 'Analyzing…' : isReading ? 'Reading sources…' : `Analyze ${ready.length || ''} source${ready.length === 1 ? '' : 's'}`.replace('  ', ' ')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SourcesStep;
